import React, { Component, ErrorInfo, ReactNode } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

interface ErrorBoundaryProps extends RouteComponentProps {
	children: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	componentName?: string;
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen?: (isNavOpen: boolean) => void;
	full?: boolean;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | string | null;
	errorInfo: ErrorInfo | string | null;
	componentName?: string;
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen?: (isNavOpen: boolean) => void;
	full?: boolean;
}

export function ErrorBoundaryHoc(
	TemplateComponent: React.ComponentType<{
		error: Error | string | null;
		errorInfo?: ErrorInfo | string | null;
		reload?: () => void;
		componentName?: string;
	}>
) {
	class ErrorBoundary extends Component<
		ErrorBoundaryProps,
		ErrorBoundaryState
	> {
		unlisten: (() => void) | null = null;

		constructor(props: ErrorBoundaryProps) {
			super(props);

			this.state = {
				hasError: false,
				error: null,
				errorInfo: null,
			};

			this.reload = this.reload.bind(this);
		}

		static getDerivedStateFromError(error: Error) {
			/**
			 * Is called during the “render” phase, so side-effects are not permitted.
			 * For those use cases, use componentDidCatch() instead.
			 */
			return { hasError: true, error };
		}

		componentDidCatch(error: Error, errorInfo: ErrorInfo) {
			/**
			 * Note
			 * In the event of an error, you can render a fallback UI with componentDidCatch() by calling setState, but this will be deprecated in a future release.
			 * Use static getDerivedStateFromError() to handle fallback rendering instead.
			 *
			 * Is called during the “commit” phase, so side-effects are permitted.
			 * It should be used for things like logging error
			 */
			this.unlisten = this.props.history.listen(() => {
				this.setState({
					hasError: false,
					error: null,
					errorInfo: null,
				});
			});

			this.setState({
				hasError: true,
				error,
				errorInfo,
			});

			if (this.props.onError) {
				this.props.onError(error, errorInfo);
			}
		}

		componentWillUnmount() {
			if (this.unlisten) {
				this.unlisten();
			}
		}

		reload() {
			this.setState({
				hasError: false,
				error: null,
				errorInfo: null,
			});

			window.location.reload();
		}

		render() {
			if (this.state.hasError) {
				return (
					<TemplateComponent
						error={this.state.error}
						errorInfo={this.state.errorInfo}
						componentName={this.state.componentName}
						reload={this.reload}
						{...this.props}
					/>
				);
			}
			return this.props.children;
		}
	}

	return withRouter(ErrorBoundary);
}
